import { useState, useEffect, useMemo, useCallback, memo } from 'react'
import { Link } from 'react-router-dom'
import { Tag } from 'lucide-react'
import { getImageDimensions } from '../utils/imageDimensions'
import { getCachedData, setCachedData } from '../utils/blogCache'

interface BlogPostSummary {
  id: number
  slug: string
  title: string
  excerpt: string
  category: string
  author: string
  date: string
  dateISO?: string
  readTime: string
  views?: string
  image: string
  tags?: string[]
}

const CACHE_KEY = 'blog_list'

const Blog = () => {
  const [posts, setPosts] = useState<BlogPostSummary[]>(() => {
    // Try to load from cache immediately for instant display
    const cached = getCachedData<BlogPostSummary[]>(CACHE_KEY)
    return cached || []
  })
  const [selectedCategory, setSelectedCategory] = useState('Tümü')
  const [loading, setLoading] = useState(() => {
    // Only show loading if we don't have cached data
    return !getCachedData<BlogPostSummary[]>(CACHE_KEY)
  })
  const [error, setError] = useState<string | null>(null)
  const [visibleCount, setVisibleCount] = useState(12) // Initial visible posts

  useEffect(() => {
    let isMounted = true
    const fetchPosts = async () => {
      // Check cache first
      const cached = getCachedData<BlogPostSummary[]>(CACHE_KEY)
      if (cached && isMounted) {
        setPosts(cached)
        setLoading(false)
        // Still fetch in background to update cache
        fetchPostsFromAPI()
        return
      }

      await fetchPostsFromAPI()
    }

    const fetchPostsFromAPI = async () => {
      try {
        const response = await fetch('/content/blog/list.json', {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          cache: 'default', // Use browser cache
        })
        if (!response.ok) {
          throw new Error('Blog içerikleri yüklenemedi.')
        }
        const data: BlogPostSummary[] = await response.json()
        if (isMounted) {
          const sorted = data
            .slice()
            .sort((a, b) => {
              const aDate = a.dateISO ? new Date(a.dateISO).getTime() : new Date(a.date).getTime()
              const bDate = b.dateISO ? new Date(b.dateISO).getTime() : new Date(b.date).getTime()
              return bDate - aDate
            })
          setPosts(sorted)
          // Cache the sorted data
          setCachedData(CACHE_KEY, sorted)
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Bilinmeyen bir hata oluştu.')
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchPosts()
    return () => {
      isMounted = false
    }
  }, [])

  const categories = useMemo(() => {
    const unique = new Set<string>()
    posts.forEach((post) => unique.add(post.category))
    return ['Tümü', ...Array.from(unique)]
  }, [posts])

  const filteredPosts = useMemo(() => {
    if (selectedCategory === 'Tümü') return posts
    return posts.filter((post) => post.category === selectedCategory)
  }, [posts, selectedCategory])

  const visiblePosts = useMemo(() => {
    return filteredPosts.slice(0, visibleCount)
  }, [filteredPosts, visibleCount])

  const hasMore = visibleCount < filteredPosts.length

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!hasMore) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleCount((prev) => Math.min(prev + 6, filteredPosts.length))
          }
        })
      },
      {
        rootMargin: '200px', // Start loading 200px before reaching the bottom
      }
    )

    const sentinel = document.getElementById('blog-load-more-sentinel')
    if (sentinel) {
      observer.observe(sentinel)
    }

    return () => {
      if (sentinel) {
        observer.unobserve(sentinel)
      }
    }
  }, [hasMore, filteredPosts.length])

  const handleCategoryChange = useCallback((category: string) => {
    setSelectedCategory(category)
    setVisibleCount(12) // Reset visible count when category changes
    // Scroll to top smoothly
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  if (loading) {
    return (
      <div className="pt-16 bg-[#f9f9f9] min-h-screen relative">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#e5e7eb_1px,transparent_1px),linear-gradient(to_bottom,#e5e7eb_1px,transparent_1px)] bg-[size:80px_80px] [mask-image:radial-gradient(ellipse_60%_40%_at_50%_10%,#000_20%,transparent_70%)] opacity-40 pointer-events-none"></div>
        <div className="max-w-6xl mx-auto px-6 py-24 text-center relative z-10">
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white border border-gray-200 shadow-sm">
            <span className="w-3 h-3 rounded-full bg-blue-500 animate-ping" />
            <span className="text-sm font-medium text-gray-600">Blog içerikleri yükleniyor...</span>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="pt-16 bg-[#f9f9f9] min-h-screen relative">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#e5e7eb_1px,transparent_1px),linear-gradient(to_bottom,#e5e7eb_1px,transparent_1px)] bg-[size:80px_80px] [mask-image:radial-gradient(ellipse_60%_40%_at_50%_10%,#000_20%,transparent_70%)] opacity-40 pointer-events-none"></div>
        <div className="max-w-3xl mx-auto px-6 py-24 text-center relative z-10">
          <div className="bg-white border border-red-200 rounded-2xl p-8 shadow-sm">
            <h1 className="text-2xl font-bold text-red-600 mb-3">Blog yüklenemedi</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center justify-center px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              Sayfayı Yenile
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!posts.length) {
    return (
      <div className="pt-16 bg-[#f9f9f9] min-h-screen relative">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#e5e7eb_1px,transparent_1px),linear-gradient(to_bottom,#e5e7eb_1px,transparent_1px)] bg-[size:80px_80px] [mask-image:radial-gradient(ellipse_60%_40%_at_50%_10%,#000_20%,transparent_70%)] opacity-40 pointer-events-none"></div>
        <div className="max-w-3xl mx-auto px-6 py-24 text-center relative z-10">
          <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Henüz blog içeriği bulunmuyor</h1>
            <p className="text-gray-600">Yeni içerikler hazırlandığında burada görüntülenecek.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="pt-16 bg-[#f9f9f9] relative min-h-screen">
      {/* Background Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#e5e7eb_1px,transparent_1px),linear-gradient(to_bottom,#e5e7eb_1px,transparent_1px)] bg-[size:80px_80px] [mask-image:radial-gradient(ellipse_60%_40%_at_50%_10%,#000_20%,transparent_70%)] opacity-40 pointer-events-none"></div>

      {/* Hero Section */}
      <section className="py-24 relative">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center">
            <span className="inline-block text-[13px] font-medium text-[#666] bg-white px-4 py-1.5 rounded-full border border-gray-200 mb-6">
              Blog
            </span>
            <h1 className="text-[28px] md:text-[44px] font-bold text-[#111] leading-tight mb-6">
              Dijital pazarlama, üretkenlik ve büyüme üzerine içgörüler
            </h1>
            <p className="text-lg md:text-xl text-[#555] max-w-3xl mx-auto mb-8 leading-relaxed">
              Thinklyze ekibinden ürün güncellemeleri, dijital pazarlama stratejileri ve uzman görüşlerini keşfedin.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter Section */}
      <section className="pb-12 relative">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => handleCategoryChange(category)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    selectedCategory === category
                    ? 'bg-black text-white shadow-md'
                    : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="pb-24 relative">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visiblePosts.map((post, index) => (
              <BlogPostCard key={post.slug} post={post} index={index} />
            ))}
          </div>

          {/* Lazy loading sentinel */}
          {hasMore && (
            <div id="blog-load-more-sentinel" className="h-20 flex items-center justify-center">
              <div className="inline-flex items-center gap-2 text-sm text-gray-500">
                <span className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                <span>Daha fazla yazı yükleniyor...</span>
              </div>
            </div>
          )}

          {filteredPosts.length === 0 && (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-200 shadow-sm">
              <Tag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-gray-600 mb-2">Bu kategoride yazı bulunamadı</h3>
              <p className="text-gray-500">Farklı bir kategori seçerek tekrar deneyin</p>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

// Memoized Blog Post Card component for better performance
const BlogPostCard = memo(({ post, index }: { post: BlogPostSummary; index: number }) => {
  // Prefetch on hover for faster navigation
  const handleMouseEnter = useCallback(() => {
    // Prefetch the blog detail JSON
    const link = document.createElement('link')
    link.rel = 'prefetch'
    link.href = `/content/blog/${post.slug}.json`
    link.as = 'fetch'
    link.crossOrigin = 'anonymous'
    document.head.appendChild(link)
  }, [post.slug])

  return (
    <Link 
      to={`/blog/${post.slug}`}
      onMouseEnter={handleMouseEnter}
    >
      <article
        className="bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 overflow-hidden group cursor-pointer h-full flex flex-col"
      >
        {/* Content */}
        <div className="p-6 flex flex-col flex-grow">
          {/* Category & Date */}
          <div className="flex items-center justify-between mb-3">
            <span className="inline-block text-xs font-medium text-gray-600 bg-gray-100 px-2.5 py-1 rounded-md">
              {post.category}
            </span>
            <span className="text-xs text-gray-500">{post.date}</span>
          </div>
          
          {/* Title */}
          <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-3 leading-tight group-hover:text-gray-700 transition-colors line-clamp-2">
            {post.title}
          </h2>
          
          {/* Excerpt */}
          <p className="text-sm text-gray-600 mb-4 leading-relaxed line-clamp-3 flex-grow">
            {post.excerpt}
          </p>
          
          {/* Footer */}
          <div className="flex items-center gap-3 pt-4 border-t border-gray-100 mt-auto">
            <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden border border-gray-200">
              <img
                src="/images/samed-ordueri.jpeg"
                alt={post.author}
                loading="lazy"
                decoding="async"
                width={getImageDimensions('/images/samed-ordueri.jpeg')?.width}
                height={getImageDimensions('/images/samed-ordueri.jpeg')?.height}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement
                  target.style.display = 'none'
                  target.parentElement!.innerHTML = `<span class="text-white font-semibold text-xs bg-gradient-to-br from-gray-300 to-gray-400 w-full h-full flex items-center justify-center">${post.author.charAt(0)}</span>`
                }}
              />
            </div>
            <div className="flex-1">
              <div className="text-xs text-gray-900 font-medium">
                {post.author}
              </div>
            </div>
            {post.readTime && (
              <span className="text-xs text-gray-500">{post.readTime}</span>
            )}
          </div>
        </div>
      </article>
    </Link>
  )
})

BlogPostCard.displayName = 'BlogPostCard'

export default Blog



